import jsPDF from "jspdf";

/**
 * Calculates the width of the longest line in a set of text lines.
 * This ensures the bubble fits the text snugly.
 */
const getActualBoxWidth = (lines, doc) => {
  let maxWidth = 0;
  lines.forEach((line) => {
    const w = doc.getTextWidth(line);
    if (w > maxWidth) maxWidth = w;
  });
  return maxWidth + 10; // Add 10 units of padding (5 left, 5 right)
};

export const downloadChatHistory = (chatData, currentUserName, metaData) => {
  console.debug("metaData", metaData);
  const doc = new jsPDF();

  // -- Configuration --
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 14;
  let currentY = 20;
  const lineHeight = 6;

  // -- Destructure Metadata --
  let selfListing = metaData?.selfListing;
  let selfCompany = metaData?.selfCompany;
  let counterListing = metaData?.counterListing;
  let counterCompany = metaData?.counterCompany;
  let selfEntity = metaData?.selfEntity;
  let counterEntity = metaData?.counterEntity;

  console.debug("SelfData", selfEntity, counterEntity);
  // -- Helper: Draw Page Border --
  const drawPageBorder = () => {
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 0, 0); // Black border
    // Draw rect starting at (5,5) with 10 units less width/height to create a margin
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10, "S");
  };

  // Initialize first page border
  drawPageBorder();

  // ==============================
  // 1. DRAW HEADER
  // ==============================

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text("CHAT Download", marginX, currentY);

  // Date
  const today = new Date();
  const dateStr = `Date: ${today.getDate()}-${
    today.getMonth() + 1
  }-${today.getFullYear()}`;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(dateStr, pageWidth - marginX, currentY, { align: "right" });

  currentY += 15;

  // Metadata Lines
  const metaLines = [
    `Self-Listing Number - ${selfListing}`,
    `Self-Company name: ${selfCompany}`,
    `Counter Listing Number - ${counterListing}`,
    `Counter Company Name - ${counterCompany}`,
  ];

  doc.setFontSize(10);
  metaLines.forEach((line) => {
    doc.text(line, marginX, currentY);
    currentY += 7;
  });

  currentY += 8;

  // Section Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Chat History", marginX, currentY);
  currentY += 2;
  doc.setLineWidth(0.5);
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 15;

  // ==============================
  // 2. MESSAGE LOOP
  // ==============================

  chatData.forEach((msg) => {
    const isCurrentUser = msg.username === currentUserName;

    // -- A. Prepare Content --
    const contentText = msg.fileUrl ? `[File] ${msg.text}` : msg.text || "";
    const maxBubbleWidth = (pageWidth - marginX * 2) * 0.6; // Max 60% of page width

    // Set font to Bold to calculate width accurately (since we render in bold)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    const textLines = doc.splitTextToSize(contentText, maxBubbleWidth);
    const boxWidth = getActualBoxWidth(textLines, doc);
    const boxHeight = textLines.length * lineHeight + 10; // Height based on lines + padding

    // Calculate total height needed for this block:
    // EntityLabel (5) + Gap (2) + Bubble (boxHeight) + Timestamp (5) + Margin (5)
    const blockHeight = 5 + 2 + boxHeight + 5 + 5;

    // -- B. Pagination Check --
    if (currentY + blockHeight > pageHeight - 10) {
      doc.addPage();
      drawPageBorder();
      currentY = 20;
    }

    // -- C. Render Message --
    const timestamp = new Date(msg.timestamp);
    const timeStr = `${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString(
      [],
      { hour: "2-digit", minute: "2-digit", hour12: true }
    )}`;

    if (isCurrentUser) {
      // === RIGHT SIDE (Self) ===

      // 1. Entity Name (selfEntity) - Above Bubble
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      doc.text(selfEntity, pageWidth - marginX, currentY + 3, {
        align: "right",
      });
      currentY += 5;

      // 2. Bubble Box
      const xPos = pageWidth - marginX - boxWidth;

      doc.setFillColor("#fffaf6"); // Light Blue Fill
      doc.setDrawColor("#fffaf6"); // Blue-ish Border
      doc.setLineWidth(0.5);
      doc.roundedRect(xPos, currentY, boxWidth, boxHeight - 5, 3, 3, "FD");

      // 3. Text Content
      doc.setTextColor("#333333");
      doc.setFontSize(8);
      doc.setLineWidth(0.5);
      doc.text(textLines, xPos + 5, currentY + 7);

      // 4. Timestamp - Below Bubble
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor("#8c8a96"); // Grey
      doc.text(timeStr, pageWidth - marginX, currentY + boxHeight, {
        align: "right",
      });
    } else {
      // === LEFT SIDE (Counter) ===

      // 1. Entity Name (counterEntity) - Above Bubble
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      doc.text(counterEntity, marginX, currentY + 3, { align: "left" });
      currentY += 5;

      // 2. Bubble Box
      const xPos = marginX;

      doc.setFillColor(245, 245, 245); // Light Gray Fill
      doc.setDrawColor(245, 245, 245); // Gray Border
      doc.setLineWidth(0.5);
      doc.roundedRect(xPos, currentY, boxWidth, boxHeight - 5, 3, 3, "FD");

      // 3. Text Content
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(textLines, xPos + 5, currentY + 7);

      // 4. Timestamp - Below Bubble
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor("#8c8a96"); // Grey
      doc.text(timeStr, marginX, currentY + boxHeight + 5, { align: "left" });
    }

    // -- D. Advance Y Position --
    currentY += boxHeight + 12; // Gap before next message
  });

  // Save File
  doc.save("CHAT_Download.pdf");
};
